// import components
import { Icon } from '@sdb/planning-ui';

// import external libraries
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';

// import MUI components
import { Typography } from '@mui/material';
import Slider from '@mui/material/Slider';

// import styles
import {
  StyledTypographyBodyBold,
  StyledTypographyBoldHtmlFontSize
} from '../Typography/Typography.styles';

// import types
import { SourceCardProps } from './SourceCard.types';

// import utils
import { displayFlooredOrCeiledNumber } from 'utils/misc';
import {
  StyledBoxFlexAlignCenterSpaceBetween,
  StyledBoxFlexCenter,
  StyledBoxFlexRow
} from '../Box/Box.styles';
import {
  StyledHoursBox,
  StyledHoursTextField,
  StyledSliderMaxHoursBox,
  StyledSourceCardBox,
  StyledSourceCardButton,
  StyledTopInformationBox
} from './SourceCard.styles';

export default function SourceCard(props: SourceCardProps) {
  const {
    type,
    icon,
    description,
    maxHours,
    className,
    readonly,
    remainingHours,
    onChange,
    onSave
  } = props;
  const { t } = useTranslation('common');
  const [value, setValue] = useState(0);

  const onSliderChange = (e, value: number | number[]) => {
    const val = Array.isArray(value) ? value[0] : value;
    setValue(val);
    if (onChange) {
      onChange(val);
    }
  };

  const onInputChange = (valueStr: string) => {
    if (valueStr !== '' && !Number.isNaN(Number(valueStr))) {
      let value = Math.abs(parseFloat(parseFloat(valueStr).toFixed(2)));
      value = value < remainingHours ? value : remainingHours;
      setValue(value);
      if (onChange) {
        onChange(value);
      }
    } else {
      setValue(-1);
    }
  };
  const maxSliderHours = type === 'number' ? 0 : Math.max(0, props?.sliderMax);

  const isDisabledBtn =
    type === 'number'
      ? value > maxHours || value < 1
      : maxHours - value < 0 || !props.sliderMax;
  const step = maxSliderHours < 1 ? maxSliderHours : 1;
  return (
    <StyledSourceCardBox className={classNames('card', className)}>
      <StyledTopInformationBox>
        <StyledBoxFlexRow>
          {icon && <Icon type={icon} />}
          <Typography>{description}</Typography>
        </StyledBoxFlexRow>
        {type === 'slider' && (
          <StyledHoursBox>
            <Typography>{t('hours')}</Typography>
            <StyledTypographyBoldHtmlFontSize>
              {displayFlooredOrCeiledNumber(maxSliderHours - value)}
            </StyledTypographyBoldHtmlFontSize>
          </StyledHoursBox>
        )}
      </StyledTopInformationBox>
      {!readonly && (
        <StyledBoxFlexAlignCenterSpaceBetween>
          {type !== 'number' && (
            <>
              <Slider
                defaultValue={0}
                min={0}
                step={step}
                max={maxSliderHours}
                size="small"
                value={value}
                valueLabelDisplay="on"
                onChange={onSliderChange}
                disabled={!!!maxSliderHours}
              />
              <StyledSliderMaxHoursBox maxSliderHours={!!!maxSliderHours}>
                {displayFlooredOrCeiledNumber(maxSliderHours)}
              </StyledSliderMaxHoursBox>
            </>
          )}
          {type === 'number' && (
            <StyledBoxFlexCenter>
              <StyledHoursTextField
                label={t('amount')}
                type="number"
                size="small"
                variant="outlined"
                value={value === -1 ? '' : value}
                onChange={(e) => onInputChange(e.target.value)}
              />
              <StyledTypographyBodyBold>{t('hours')}</StyledTypographyBodyBold>
            </StyledBoxFlexCenter>
          )}
          <StyledSourceCardButton
            variant="contained"
            size="small"
            disabled={!value || (type !== 'export' && isDisabledBtn)}
            onClick={() => {
              onSave(value);
              setValue(0);
            }}
          >
            {type === 'export' ? t('balanceBudget.export') : t('deposit')}
          </StyledSourceCardButton>
        </StyledBoxFlexAlignCenterSpaceBetween>
      )}
    </StyledSourceCardBox>
  );
}
